import React, { memo, useMemo } from "react";
import { Box, IconButton, Typography, Link as MuiLink } from "@mui/material";
import PlaceIcon from "@mui/icons-material/Place";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import DownloadIcon from "@mui/icons-material/Download";
import { downloadChatMedia, filenameFromMediaUrl } from "../../utils/downloadChatMedia";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import CheckIcon from "@mui/icons-material/Check";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import { useTranslation } from "react-i18next";
import { formatWhatsAppText } from "../../utils/whatsappTextFormat";
import { buildWhatsappInteractivePresentation } from "../../utils/whatsappInteractiveDisplay";
import ImageMessage from "../../components/MessageHistory/ImageMessage";
import VideoMessage from "../../components/MessageHistory/VideoMessage";
import AudioMessage from "../../components/MessageHistory/AudioMessage";
import WebChatInteractive from "./WebChatInteractive";
import { getDeliveryStatusDisplay } from "../../components/MessageHistory/messageDeliveryStatus";

const BUBBLE_OUT = "#dcf8c6";
const BUBBLE_IN = "#ffffff";

function formatTime(value) {
  if (!value) return "";
  try {
    const d = new Date(String(value).replace(" ", "T"));
    if (Number.isNaN(d.getTime())) return "";
    return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  } catch {
    return "";
  }
}

function textCaption(msg) {
  const text = String(msg.text || "").trim();
  if (!text) return null;
  if (text === "[mensagem]") return null;
  if (msg.type === "audio") return null;
  if (msg.type === "image" && /^imagem$/i.test(text)) return null;
  if (msg.type === "video" && /^vídeo$/i.test(text)) return null;
  if (msg.type === "file" && msg.document?.name && text === msg.document.name) return null;
  if (msg.type === "location" && /^localiza/i.test(text)) return null;
  if (msg.type === "contact") return null;
  if (msg.type === "sticker") return null;
  return text;
}

function hasInteractiveContent(meta) {
  if (!meta || typeof meta !== "object") return false;
  if (Array.isArray(meta.buttons) && meta.buttons.length) return true;
  if (Array.isArray(meta.linkButtons) && meta.linkButtons.length) return true;
  if (meta.listMessage) return true;
  if (meta.buttonMessage) return true;
  if (meta.interactiveOptions && typeof meta.interactiveOptions === "object") {
    const url = meta.interactiveOptions.ctaUrl || meta.interactiveOptions.url;
    if (url) return true;
    if (meta.interactiveOptions.mode === "ticket_flow_web" && meta.interactiveOptions.flowToken) {
      return true;
    }
  }
  if (meta.carouselMessage && Array.isArray(meta.carouselMessage.cards) && meta.carouselMessage.cards.length) {
    return true;
  }
  return false;
}

function mergeInteractiveMetadata(msg) {
  const base =
    msg.chatbotMetadata && typeof msg.chatbotMetadata === "object"
      ? { ...msg.chatbotMetadata }
      : {};

  const pres = buildWhatsappInteractivePresentation(
    { ...msg, channel: "whatsapp", json_whatsapp: msg.json_whatsapp },
    null,
    null
  );
  if (pres?.clientFacing?.mode === "carousel" && pres.clientFacing.items?.length) {
    base.carouselMessage = {
      cards: pres.clientFacing.items.map((item) => ({
        buttonTitle: item.label,
        body: item.description,
        imageUrl: item.imageUrl,
        postback: item.id,
      })),
    };
  }
  if (pres?.outboundOptions?.length && !base.buttons?.length) {
    base.buttons = pres.outboundOptions.map((o) => o.title || o.id).filter(Boolean);
  }
  return hasInteractiveContent(base) ? base : null;
}

function Tail({ fromClient }) {
  return (
    <Box
      aria-hidden
      sx={{
        position: "absolute",
        top: 0,
        [fromClient ? "right" : "left"]: -6,
        width: 0,
        height: 0,
        borderStyle: "solid",
        borderWidth: fromClient ? "0 0 10px 8px" : "0 8px 10px 0",
        borderColor: fromClient
          ? `transparent transparent transparent ${BUBBLE_OUT}`
          : `transparent ${BUBBLE_IN} transparent transparent`,
        filter: "drop-shadow(0 1px 0.5px rgba(0,0,0,0.08))",
      }}
    />
  );
}

function WebChatMessageBubble({
  msg,
  showInteractive = false,
  sending = false,
  onQuickReply,
  onOpenTicketFlow,
  galleryUrls,
  galleryIndex = 0,
}) {
  const { t } = useTranslation("chat");
  const fromClient = Boolean(msg.isFromClient || msg.sender === "client");
  const caption = textCaption(msg);
  const html = useMemo(() => {
    if (!caption) return "";
    try {
      return formatWhatsAppText(caption);
    } catch {
      return caption;
    }
  }, [caption]);

  const metadata = useMemo(() => mergeInteractiveMetadata(msg), [msg]);
  const hasInteractive = Boolean(showInteractive && metadata);
  const hasLocation =
    Number.isFinite(msg.latitude) &&
    Number.isFinite(msg.longitude) &&
    !(msg.latitude === 0 && msg.longitude === 0);

  const mapsUrl = hasLocation
    ? `https://maps.google.com/?q=${msg.latitude},${msg.longitude}`
    : null;

  const contact = msg.contact && typeof msg.contact === "object" ? msg.contact : null;
  const isContact = msg.type === "contact" || Boolean(contact?.name || contact?.phone);
  const quotedText =
    msg.quotedMessageConversationWhatsapp != null
      ? String(msg.quotedMessageConversationWhatsapp).trim()
      : "";
  const stickerUrl =
    msg.type === "sticker" ? msg.image || msg.sticker || msg.file || null : null;
  const delivery = fromClient ? getDeliveryStatusDisplay(msg) : null;
  const isPendingSend =
    fromClient &&
    (msg._optimistic || delivery?.icon === "pending" || delivery?.isPending);
  const tickIcon = fromClient ? (
    isPendingSend ? (
      <CheckIcon sx={{ fontSize: 15, ml: 0.25, color: "#8696a0" }} />
    ) : (
      <DoneAllIcon
        sx={{
          fontSize: 15,
          ml: 0.25,
          color: delivery?.icon === "read" ? "#53bdeb" : "#8696a0",
        }}
      />
    )
  ) : null;

  const timeBlock = (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        float: "right",
        ml: 1,
        mt: 0.35,
        position: "relative",
        top: 2,
        gap: 0.15,
      }}
    >
      <Typography
        component="span"
        sx={{ fontSize: "0.68rem", color: "#667781", lineHeight: 1, whiteSpace: "nowrap" }}
      >
        {formatTime(msg.createdAt)}
      </Typography>
      {tickIcon}
    </Box>
  );

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: fromClient ? "flex-end" : "flex-start",
        px: { xs: 1.5, sm: 2 },
        mb: hasInteractive ? 1.25 : 0.75,
      }}
    >
      <Box
        sx={{
          maxWidth: { xs: "82%", sm: "min(420px, 70%)" },
          width: "fit-content",
          minWidth: hasInteractive ? { xs: "72%", sm: 260 } : 0,
          position: "relative",
        }}
      >
        <Box
          sx={{
            position: "relative",
            bgcolor: fromClient ? BUBBLE_OUT : BUBBLE_IN,
            borderRadius: fromClient ? "7.5px 0 7.5px 7.5px" : "0 7.5px 7.5px 7.5px",
            px: 1.25,
            pt: 0.7,
            pb: hasInteractive ? 0 : 0.55,
            boxShadow: "0 1px 0.5px rgba(11,20,26,0.13)",
            overflow: "visible",
          }}
        >
          {!stickerUrl ? <Tail fromClient={fromClient} /> : null}

          {quotedText ? (
            <Box
              sx={{
                mb: 0.65,
                pl: 1,
                borderLeft: "4px solid",
                borderColor: fromClient ? "#34b7a7" : "#06cf9c",
                bgcolor: fromClient ? "rgba(0,0,0,0.04)" : "rgba(0,0,0,0.05)",
                borderRadius: "4px",
                py: 0.45,
                pr: 0.75,
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  display: "block",
                  color: "#667781",
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",
                  lineHeight: 1.35,
                }}
              >
                {quotedText.length > 180 ? `${quotedText.slice(0, 180)}…` : quotedText}
              </Typography>
            </Box>
          ) : null}

          {stickerUrl ? (
            <Box
              component="img"
              src={stickerUrl}
              alt={t("webchat.stickerAlt")}
              sx={{
                display: "block",
                maxWidth: 148,
                maxHeight: 148,
                mb: 0.25,
                userSelect: "none",
              }}
            />
          ) : null}

          {!stickerUrl && msg.type === "image" && msg.image ? (
            <Box sx={{ mb: caption ? 0.6 : 0, mx: -0.35, mt: -0.15, borderRadius: 1, overflow: "hidden" }}>
              <ImageMessage
                message={{ ...msg, image: msg.image, url: msg.image }}
                galleryUrls={galleryUrls}
                galleryIndex={galleryIndex}
              />
            </Box>
          ) : null}

          {msg.type === "video" && msg.video ? (
            <Box sx={{ mb: caption ? 0.6 : 0 }}>
              <VideoMessage message={{ ...msg, url: msg.video, video: msg.video }} />
            </Box>
          ) : null}

          {msg.type === "audio" && msg.audio ? (
            <Box sx={{ mb: caption ? 0.4 : 0, minWidth: { xs: 220, sm: 250 } }}>
              <AudioMessage message={{ ...msg, audio: msg.audio, url: msg.audio }} />
            </Box>
          ) : null}

          {(msg.type === "file" || msg.document?.url || msg.file) &&
          (msg.document?.url || msg.file) ? (
            <Box
              sx={{
                mb: caption ? 0.6 : 0,
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                bgcolor: "rgba(0,0,0,0.04)",
                borderRadius: 1,
                px: 1,
                py: 0.5,
              }}
            >
              <MuiLink
                href={msg.document?.url || msg.file}
                target="_blank"
                rel="noopener noreferrer"
                underline="none"
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  color: "inherit",
                  flex: 1,
                  minWidth: 0,
                }}
              >
                <InsertDriveFileOutlinedIcon sx={{ color: "#54656f", flexShrink: 0 }} />
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 500, wordBreak: "break-word", color: "#027eb5" }}
                >
                  {msg.document?.name || caption || "Arquivo"}
                </Typography>
              </MuiLink>
              <IconButton
                size="small"
                aria-label={t("webchat.downloadAria")}
                onClick={() => {
                  const fileUrl = msg.document?.url || msg.file;
                  const fileName =
                    msg.document?.name || filenameFromMediaUrl(fileUrl, "arquivo");
                  downloadChatMedia(fileUrl, fileName);
                }}
              >
                <DownloadIcon fontSize="small" />
              </IconButton>
            </Box>
          ) : null}

          {(msg.type === "location" || hasLocation) && hasLocation ? (
            <MuiLink
              href={mapsUrl}
              target="_blank"
              rel="noopener noreferrer"
              underline="none"
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.75,
                mb: caption ? 0.6 : 0,
                color: "inherit",
                bgcolor: "rgba(0,0,0,0.04)",
                borderRadius: 1,
                px: 1,
                py: 0.75,
              }}
            >
              <PlaceIcon fontSize="small" sx={{ color: "#e53935" }} />
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {t("webchat.viewLocation")}
              </Typography>
            </MuiLink>
          ) : null}

          {isContact ? (
            <Box
              sx={{
                display: "flex",
                alignItems: "flex-start",
                gap: 1,
                mb: caption ? 0.6 : 0,
                bgcolor: "rgba(0,0,0,0.04)",
                borderRadius: 1,
                px: 1,
                py: 0.85,
              }}
            >
              <PersonOutlineIcon sx={{ color: "#54656f", mt: 0.15, flexShrink: 0 }} />
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, wordBreak: "break-word" }}>
                  {contact?.name || t("webchat.contactFallback")}
                </Typography>
                {contact?.phone ? (
                  <Typography
                    variant="caption"
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.5,
                      color: "#667781",
                      mt: 0.25,
                    }}
                  >
                    <PhoneOutlinedIcon sx={{ fontSize: 14 }} />
                    {contact.phone}
                  </Typography>
                ) : null}
              </Box>
            </Box>
          ) : null}

          {html ? (
            <Box
              sx={{
                fontSize: "0.9375rem",
                lineHeight: 1.35,
                color: "#111b21",
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
                "& a": { color: "#027eb5" },
                overflow: "hidden",
              }}
            >
              <Box component="span" dangerouslySetInnerHTML={{ __html: html }} />
              {timeBlock}
            </Box>
          ) : null}

          {!html &&
          !stickerUrl &&
          !msg.image &&
          !msg.video &&
          !msg.audio &&
          !msg.file &&
          !msg.document?.url &&
          !hasLocation &&
          !isContact &&
          msg.type !== "location" ? (
            <Box sx={{ overflow: "hidden" }}>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}
              >
                <InsertDriveFileOutlinedIcon sx={{ fontSize: 16 }} />
                {t("webchat.fallbackMessage")}
              </Typography>
              {timeBlock}
            </Box>
          ) : null}

          {!html &&
          (stickerUrl ||
            msg.image ||
            msg.video ||
            msg.audio ||
            msg.file ||
            msg.document?.url ||
            hasLocation ||
            isContact) ? (
            <Box sx={{ clear: "both", textAlign: "right", mt: 0.15 }}>{timeBlock}</Box>
          ) : null}

          {hasInteractive ? (
            <WebChatInteractive
              metadata={metadata}
              disabled={sending}
              onSelect={(label) => onQuickReply?.(label)}
              onOpenTicketFlow={onOpenTicketFlow}
            />
          ) : null}
        </Box>
      </Box>
    </Box>
  );
}

function areEqual(prev, next) {
  return (
    prev.msg === next.msg &&
    prev.showInteractive === next.showInteractive &&
    prev.sending === next.sending &&
    prev.galleryIndex === next.galleryIndex &&
    prev.galleryUrls === next.galleryUrls &&
    prev.onOpenTicketFlow === next.onOpenTicketFlow
  );
}

export default memo(WebChatMessageBubble, areEqual);
